<template>
  <div class="landing-page">
    <div class="section section--first">
      <div class="language">
        <div
          class="language__switch"
          @click="toggleLanguageDropdown"
          v-click-outside="onClickOutside"
        >
          <fa class="pr-8" icon="globe-asia"></fa>
          <span class="pr-16">{{ currentLanguage.name }}</span>
          <fa
            icon="chevron-down"
            :class="{ 'fa-rotate-180': isShowLanguageDropdown }"
          ></fa>
        </div>
        <div v-if="isShowLanguageDropdown" class="language__dropdown">
          <a
            class="dropdown-item lang_menu"
            v-for="lang of selectableLanguages"
            :key="lang.locale"
            @click="changeLocale(lang)"
          >
            {{ lang.name }}
          </a>
        </div>
      </div>
      <div class="section__content">
        <h1 class="title">{{ $t('searchBox.title') }}</h1>
        <h2 class="subtitle">{{ $t('searchBox.subtitle') }}</h2>
        <p class="description">{{ $tc('searchBox.subHeader') }}</p>
        <SearchBox
          class="search-box-wrapper"
          :isAutoFocus="true"
          :canGetNetworkInfo="false"
        ></SearchBox>
      </div>
      <div class="discover-more-link">
        <div
          v-see-more="{
            el: '#see-more',
            duration: 300,
            easing: 'linear',
            offset: -100,
          }"
          class="see-more-link"
        >
          {{ $t('welcomePage.discoverMore') }}
          <br />
          <fa icon="chevron-down"></fa>
        </div>
      </div>
    </div>
    <div class="section section--second">
      <div id="see-more" class="section__content">
        <h2 class="title">{{ $t('welcomePage.tools.title') }}</h2>
        <div class="description">
          <b>{{ $t('welcomePage.tools.description') }}</b>
        </div>
        <div class="tabs--desktop">
          <div
            v-for="(tab, tabId) in tabs"
            class="tab"
            :class="tabClass(tabId)"
            @click="onTabClick(tabId, tab.info)"
            :key="tab.name"
          >
            <span class="tab__title">{{ tab.name }}</span>
          </div>
          <div class="tab__description">{{ activeTabInfo }}</div>
        </div>
        <v-touch
          v-on:swipeleft="previousTab()"
          v-on:swiperight="nextTab()"
          class="tabs--mobile"
        >
          <div class="tabs__selection">
            <fa icon="chevron-left" @click="previousTab()"></fa>
            <strong>{{ activeTabName }}</strong>
            <div class="tab__description">{{ activeTabInfo }}</div>
            <fa icon="chevron-right" @click="nextTab()"></fa>
          </div>
          <div class="tabs__dots">
            <div
              v-for="(tab, tabId) in tabs"
              :class="tabClass(tabId)"
              @click="onTabClick(tabId, tab.info)"
              :key="tab.name"
            ></div>
          </div>
        </v-touch>

        <SearchBox
          :active-tab="activeTabId"
          :show-info="false"
          :canGetNetworkInfo="false"
        ></SearchBox>
      </div>
    </div>
    <div class="section section--third">
      <div class="section__content">
        <div class="content-list">
          <div class="content-list__separator"></div>
          <h2 class="content-list__title">
            {{ $tc('welcomePage.yourNetworkInfo.title') }}
          </h2>
          <div class="content-list__subtitle">
            {{ $tc('welcomePage.yourNetworkInfo.description') }}
          </div>
          <ul class="content-list__list">
            <li
              v-for="(item, index) in $t('welcomePage.yourNetworkInfo.items')"
              :key="item.text"
              class="content-list__list-item"
            >
              {{ item.text }}
              <br />
              <router-link
                class="content-list__link"
                tag="a"
                :to="tabLink(1, index)"
              >
                {{ item.link }}
              </router-link>
            </li>
          </ul>
        </div>
        <div class="content-list">
          <div
            class="content-list__separator content-list__separator--long"
          ></div>
          <h2 class="content-list__title">
            {{ $tc('welcomePage.otherNetworkInfo.title') }}
          </h2>
          <div class="content-list__subtitle">
            {{ $tc('welcomePage.otherNetworkInfo.description') }}
          </div>
          <ul class="content-list__list">
            <li
              class="content-list__list-item"
              v-for="(item, index) in $t('welcomePage.otherNetworkInfo.items')"
              :key="item.text"
            >
              {{ item.text }}
              <br />
              <router-link
                class="content-list__link"
                tag="a"
                :to="tabLink(2, index)"
              >
                {{ item.link }}
              </router-link>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="section section--forth">
      <div class="section__content">
        <h2 class="title">{{ $t('welcomePage.startUsing') }}</h2>
        <SearchBox :show-info="false" :canGetNetworkInfo="false"></SearchBox>
        <div class="powered-text">
          {{ $tc('welcomePage.poweredBy', 1) }}
          <a href="https://stat.ripe.net/" target="_blank">
            {{ $tc('welcomePage.poweredBy', 2) }}
          </a>
        </div>
        <div
          v-if="currentLanguage.locale === 'ja'"
          class="translation-provided"
        >
          <a href="https://www.nic.ad.jp/" target="_blank">
            {{ $tc('welcomePage.JaTranslationBy', 1) }}
          </a>
          {{ $tc('welcomePage.JaTranslationBy', 2) }}
        </div>
      </div>
    </div>
    <Footer></Footer>
  </div>
</template>

<script>
import scrollto from 'vue-scrollto';
import * as vClickOutside from 'v-click-outside';
import i18n from '../i18n';
import Footer from '../components/ui/Footer.vue';
import SearchBox from '../components/SearchBox.vue';
import languageConfig from '../config/language';

export default {
  directives: {
    seeMore: scrollto,
    clickOutside: vClickOutside.directive,
  },
  data() {
    return {
      activeTabId: 'apnic-at-a-glance',
      isShowLanguageDropdown: false,
      languages: languageConfig.options,
      currentLanguage: { name: 'English', locale: 'en' },
    };
  },
  computed: {
    tabs() {
      const data = JSON.parse(JSON.stringify(this.$t('tabs')));
      delete data['apnic-experimental'];
      delete data['apnic-comparison'];
      return data;
    },
    networkInfo() {
      return this.$store.state.networkInfo;
    },
    activeTabName() {
      return this.tabs[this.activeTabId].name;
    },
    activeTabInfo() {
      return this.tabs[this.activeTabId].info;
    },
    tabsArray() {
      return Object.keys(this.tabs).map((i) => ({
        id: i,
        ...this.tabs[i],
      }));
    },
    selectableLanguages() {
      return this.languages.filter(
        (i) => i.locale !== this.currentLanguage.locale
      );
    },
  },
  mounted() {
    this.$store.commit('setShowSideBar', false);

    const initialLanguage = languageConfig.init();

    if (initialLanguage) this.currentLanguage = initialLanguage;
  },
  methods: {
    tabClass(tab) {
      return tab == this.activeTabId ? 'tab--active' : 'tab--inactive';
    },
    tabLink(category, index) {
      let info = { ip: '8.8.8.8', prefix: '8.8.8.0/24', asn: '15169' };
      if (this.$store.state.networkInfo) {
        if (this.$store.state.networkInfo.prefix)
          info.prefix = this.$store.state.networkInfo.prefix;
        if (this.$store.state.networkInfo.asn)
          info.asn = this.$store.state.networkInfo.asn;
        if (this.$store.state.networkInfo.ip)
          info.ip = this.$store.state.networkInfo.ip;
      }
      let item = {
        10: ['routing', info.prefix],
        11: ['routing', info.prefix],
        12: ['routing', info.prefix],
        13: ['anti-abuse', info.prefix],
        14: ['geo', info.prefix],
        20: ['at-a-glance', 'AS' + info.asn],
        21: ['routing', 'AS' + info.asn],
        22: ['rqc', 'AS' + info.asn],
        23: ['routing', info.prefix],
        24: ['anti-abuse', info.prefix],
      }[category + '' + index];
      let cat = category === 1 ? 'yourNetworkInfo' : 'otherNetworkInfo';
      return (
        '/search/' +
        item[1] +
        '#tab=apnic-' +
        item[0] +
        '&category=' +
        cat +
        '&index=' +
        index
      );
    },
    onTabClick(tabId) {
      this.activeTabId = tabId;
    },
    nextTab() {
      const currentId = this.activeTabId;
      const index = this.tabsArray.findIndex((i) => i.id === currentId);
      if (index === -1) {
        console.error('Tab not found');
        return;
      }
      const nextTabIndex = index + 1 >= this.tabsArray.length ? 0 : index + 1;
      const nextTab = this.tabsArray[nextTabIndex];
      this.onTabClick(nextTab.id, nextTab.info);
    },
    previousTab() {
      const currentId = this.activeTabId;
      const index = this.tabsArray.findIndex((i) => i.id === currentId);
      if (index === -1) {
        console.error('Tab not found');
        return;
      }
      const nextTabIndex =
        index - 1 < 0 ? this.tabsArray.length - 1 : index - 1;
      const nextTab = this.tabsArray[nextTabIndex];
      this.onTabClick(nextTab.id, nextTab.info);
    },
    toggleLanguageDropdown() {
      this.isShowLanguageDropdown = !this.isShowLanguageDropdown;
    },
    changeLocale(lang) {
      // Trigger Event for GTM to track the selected language
      window.dataLayer.push({
        event: 'selected-language',
        selectedLanguage: lang.locale,
      });

      this.currentLanguage = lang;
      localStorage.setItem('lang', JSON.stringify(lang));
      i18n.locale = lang.locale;
      window.changeLocale(i18n.locale);
      this.isShowLanguageDropdown = false;
    },
    onClickOutside() {
      setTimeout(() => {
        this.isShowLanguageDropdown = false;
      }, 200);
    },
  },
  created() {
    this.$store.dispatch('getNetworkInfo');
  },
  components: {
    SearchBox,
    Footer,
  },
};
</script>

<style scoped lang="scss">
$tablet-width: 719px;
$tablet-bp: 768px;
$mobile-width: 453px;

h1 {
  font-size: 40px;
  font-weight: bold;
  margin-bottom: 8px;

  @media screen and (max-width: $tablet-bp) {
    font-size: 26px;
  }
}

h2 {
  font-size: 32px;
  font-weight: bold;

  margin-bottom: 24px;

  @media screen and (max-width: $tablet-bp) {
    font-size: 20px;
  }
}

.section {
  display: flex;
  justify-content: center;
  align-items: center;
  // max-width: 1160px;
  margin: 0 auto;

  &__content {
    max-width: $tablet-width;

    @media screen and (max-width: $tablet-bp) {
      max-width: $mobile-width;
      padding: 0 30px;
    }
  }

  &--first {
    height: calc(100vh - 50px);
    text-align: center;
    position: relative;
    width: 100%;

    .language {
      position: absolute;
      top: 1rem;
      left: 1rem;
      height: 2rem;
      color: $text-black;

      &__switch {
        width: 8rem;
        height: 100%;
        display: flex;
        justify-content: space-around;
        align-items: center;
        cursor: pointer;
      }

      &__dropdown {
        width: 8rem;
        background-color: #ccc;
        cursor: pointer;

        .lang_menu {
          color: $text-black;
          height: 3rem;
          line-height: 3rem;
          padding: 0;
          text-align: center;

          &:hover {
            color: white;
            background-color: #666;
          }
        }
      }
    }

    .section__content {
      @media screen and (max-width: $tablet-bp) {
        padding-bottom: 100px;
      }
    }

    h2 {
      margin-top: 0;
    }

    p {
      font-size: 20px;
      margin-bottom: 24px;

      @media screen and (max-width: $tablet-bp) {
        font-size: 16px;
      }
    }

    .search-box-wrapper {
      ::v-deep .search-box {
        margin: 0 auto;
      }
    }

    .discover-more-link {
      position: absolute;
      bottom: 50px;
      width: 120px;
      left: 50%;
      margin-left: -60px;
      cursor: pointer;

      @media screen and (max-width: $tablet-bp) {
        bottom: 70px;
      }
    }
  }

  &--second {
    background: #003957;
    color: white;
    padding: 60px 0;

    .title {
      color: #fff;
      margin-bottom: 36px;
    }

    .description {
      margin-bottom: 40px;
      margin-top: 12px;
    }

    .tabs {
      &__selection {
        position: relative;
        display: flex;
        justify-content: flex-start;

        align-items: center;
        margin-bottom: 15px;

        span {
          text-align: center;
          width: 140px;
        }
      }

      &__dots {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 150px;
        margin: 0 auto;

        .active-tab,
        .inactive-tab {
          height: 3px;
          background: white;
          width: 15px;
        }

        .active-tab {
          height: 5px;
          background: $apnic-blue;
        }
      }

      &--desktop {
        .tab {
          display: inline-block;
          border-bottom: 2px solid white;
          padding: 0 9px;
          padding-bottom: 1px;

          &--active {
            user-select: none;
            border-color: #007bbd;
            border-width: 4px;
            padding-bottom: 0px;
            position: relative;

            &::after {
              content: '';
              position: absolute;
              width: 0;
              height: 0;
              border-width: 10px;
              border-style: solid;
              border-color: #007bbd transparent transparent transparent;
              left: 50%;
              bottom: -100%;
              margin-left: -10px;
            }
          }

          &--inactive {
            cursor: pointer;
          }

          &__description {
            margin: 36px 0;
            width: 699px;
          }
        }

        @media screen and (max-width: $tablet-bp) {
          display: none;
        }
      }

      &--mobile {
        margin-bottom: 20px;
        display: none;

        @media screen and (max-width: $tablet-bp) {
          display: block;
        }

        .tabs__selection {
          display: flex;
          flex-direction: column;
          align-items: flex-start;

          .tab__description {
            max-width: $mobile-width;
          }
        }

        svg {
          position: absolute;
        }

        svg[data-icon='chevron-left'] {
          left: -28px;
          top: 30px;
          font-size: 28px;
        }

        svg[data-icon='chevron-right'] {
          right: -28px;
          top: 30px;
          font-size: 28px;
        }
      }
    }
  }

  &--third {
    padding: 40px 0;

    .section__content {
      display: flex;

      @media screen and (max-width: $tablet-bp) {
        flex-wrap: wrap;
      }
    }

    .content-list {
      max-width: 359px;
      margin-bottom: 24px;

      @media screen and (max-width: $tablet-bp) {
        max-width: $mobile-width;

        &__list {
          max-width: 363px;
          margin-left: auto;
        }
      }

      &__separator {
        width: 57px;
        height: 4px;
        background: #007bbd;

        &--long {
          width: 71px;
        }
      }

      &__title {
        font-size: 24px;
        font-weight: 700;
        margin-bottom: 32px;
        margin-top: 24px;
      }

      &__subtitle {
        font-size: 16px;
        font-weight: 600;
        min-height: 28px;
        margin-bottom: 24px;
      }

      &__list {
        list-style: none;
        padding-inline-start: 16px;

        li {
          margin-bottom: 24px;
        }

        li::before {
          content: '';
          background-color: #004971;
          font-weight: 700;
          display: inline-block;
          width: 6px;
          height: 6px;
          margin-bottom: 3px;
          margin-right: 6px;
          margin-left: -16px;
          border-radius: 50%;
        }
      }
    }
  }

  &--forth {
    padding: 100px 0;
    background: #f5f5f5;
    text-align: center;

    @media screen and (max-width: $tablet-bp) {
      padding: 50px 0;
    }

    .title {
      @media screen and (max-width: $tablet-bp) {
        text-align: left;
      }
    }

    ::v-deep .search-box {
      margin: 0 auto;
    }

    .powered-text {
      margin-top: 160px;

      @media screen and (max-width: $tablet-bp) {
        margin-top: 100px;
      }
    }

    .translation-provided {
      margin-top: 0.75rem;
    }
  }

  #see-more {
    ::v-deep .invalid-input-warning {
      color: white;
    }
  }
}
</style>
